/** A Tanger tile layer as the WeHunt APIs serve it (`/api/maps` catalogue entry). */
export interface TangerTileSpec {
  url: string
  generation: number
  /** Tile widths the API advertises. Empty when unknown. */
  widths: number[]
  minZ?: number
  maxZ?: number
}

export interface RasterSourceSpec {
  type: 'raster'
  tiles: string[]
  tileSize: number
  minzoom?: number
  maxzoom?: number
}

const DEFAULT_TILE_SIZE = 256

/**
 * The tile width to request, in pixels.
 *
 * `preferredWidth` selects the largest width the layer offers that does not
 * exceed it; without one, the layer's first offered width wins. A larger tile
 * covers proportionally more ground, so MapLibre requests one zoom level lower
 * and the same view costs a quarter of the requests at 512 — which matters
 * because the Tanger tileserver admits a bounded number of concurrent requests
 * and rejects the overflow with 503 during a pan.
 */
export function tileSizeOf(spec: TangerTileSpec, preferredWidth?: number): number {
  const offered = spec.widths ?? []
  if (preferredWidth !== undefined) {
    const withinPreference = offered.filter((w) => w <= preferredWidth)
    if (withinPreference.length > 0) return Math.max(...withinPreference)
  }
  return offered[0] ?? DEFAULT_TILE_SIZE
}

/**
 * Converts a Tanger tile template into one MapLibre can substitute.
 *
 * Tanger's template is `{G}/{W}/{Z}/{Y}/{X}`: generation and tile width are
 * constants for a given layer, so they are baked in here, while MapLibre fills
 * the remaining three. Note that Tanger orders Y before X, the opposite of the
 * usual z/x/y convention — renaming in place rather than reassembling the path
 * is what keeps that ordering intact.
 */
export function toMapLibreTileUrl(spec: TangerTileSpec, preferredWidth?: number): string {
  return spec.url
    .replace('{G}', String(spec.generation))
    .replace('{W}', String(tileSizeOf(spec, preferredWidth)))
    .replace('{Z}', '{z}')
    .replace('{Y}', '{y}')
    .replace('{X}', '{x}')
}

/** A MapLibre raster source for a Tanger layer. Both the URL's baked width and
 *  `tileSize` must agree, or MapLibre lays the tiles out at the wrong scale. */
export function tangerRasterSource(
  spec: TangerTileSpec,
  preferredWidth?: number,
): RasterSourceSpec {
  return {
    type: 'raster',
    tiles: [toMapLibreTileUrl(spec, preferredWidth)],
    tileSize: tileSizeOf(spec, preferredWidth),
    minzoom: spec.minZ,
    maxzoom: spec.maxZ,
  }
}
